import type { Bounds, Size, Vec2 } from '@/types';

export const boundsFrom = (minX: number, minY: number, maxX: number, maxY: number): Bounds => ({
  minX,
  minY,
  maxX,
  maxY,
});

export const boundsFromPositionSize = (position: Vec2, size: Size): Bounds => ({
  minX: position.x,
  minY: position.y,
  maxX: position.x + size.width,
  maxY: position.y + size.height,
});

export const boundsWidth = (b: Bounds): number => b.maxX - b.minX;
export const boundsHeight = (b: Bounds): number => b.maxY - b.minY;
export const boundsCenter = (b: Bounds): Vec2 => ({
  x: (b.minX + b.maxX) / 2,
  y: (b.minY + b.maxY) / 2,
});

export const containsPoint = (b: Bounds, p: Vec2): boolean =>
  p.x >= b.minX && p.x <= b.maxX && p.y >= b.minY && p.y <= b.maxY;

export const intersects = (a: Bounds, b: Bounds): boolean =>
  a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;

/** True when `inner` is entirely inside `outer` (touching edges count as inside). */
export const containsBounds = (outer: Bounds, inner: Bounds): boolean =>
  inner.minX >= outer.minX && inner.maxX <= outer.maxX && inner.minY >= outer.minY && inner.maxY <= outer.maxY;

export const union = (a: Bounds, b: Bounds): Bounds => ({
  minX: Math.min(a.minX, b.minX),
  minY: Math.min(a.minY, b.minY),
  maxX: Math.max(a.maxX, b.maxX),
  maxY: Math.max(a.maxY, b.maxY),
});

export const expand = (b: Bounds, margin: number): Bounds => ({
  minX: b.minX - margin,
  minY: b.minY - margin,
  maxX: b.maxX + margin,
  maxY: b.maxY + margin,
});

export const translate = (b: Bounds, delta: Vec2): Bounds => ({
  minX: b.minX + delta.x,
  minY: b.minY + delta.y,
  maxX: b.maxX + delta.x,
  maxY: b.maxY + delta.y,
});

/** Union of many bounds, or `null` for an empty list. */
export const unionAll = (list: readonly Bounds[]): Bounds | null => {
  const [first, ...rest] = list;
  return first === undefined ? null : rest.reduce(union, first);
};

/** Axis-aligned bounds enclosing a set of points. */
export const boundsOfPoints = (points: readonly Vec2[]): Bounds => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
};
